import { translate } from "@atoms/atom-client/i18n";
import type { IMeshBounds } from "@project/emerge-types";
import { ReferenceGuideType } from "@project/emerge-types";
import { OrbitControls, PivotControls } from "@react-three/drei";
import {
  Canvas,
  type RootState,
  type ThreeEvent,
  useThree,
} from "@react-three/fiber";
import type React from "react";
import {
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Box3,
  type Group,
  type Material,
  Matrix3,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
  type PerspectiveCamera,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
} from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { ProductViewerStage } from "../new/ProductViewerStage";
import { markProductSceneChanged } from "../new/sceneActivity";
import { ThreeToolsInstance } from "../util/ThreeTools";
import { getWorldAxisDirection as getWorldAxisDirectionFromModel } from "./meshAutoAlign";
import {
  createMeshEditorModel,
  type MeshEditorModelResources,
  type MeshEditorTransform,
} from "./meshEditorModel";

// Define orientation types
type OrientationName = "FRONT" | "BACK" | "UP" | "DOWN" | "RIGHT" | "LEFT";
type OrientationData = {
  name: OrientationName;
  rotation: [number, number, number];
};

// Define the six possible cube orientations
const ORIENTATIONS: OrientationData[] = [
  { name: "FRONT", rotation: [0, 0, 0] },
  { name: "BACK", rotation: [0, ThreeToolsInstance.DEG2RAD(180), 0] },
  { name: "UP", rotation: [ThreeToolsInstance.DEG2RAD(90), 0, 0] },
  { name: "DOWN", rotation: [ThreeToolsInstance.DEG2RAD(-90), 0, 0] },
  { name: "RIGHT", rotation: [0, ThreeToolsInstance.DEG2RAD(-90), 0] },
  { name: "LEFT", rotation: [0, ThreeToolsInstance.DEG2RAD(90), 0] },
];

// Define the four possible rotations around the current orientation axis (in degrees)
const ROTATIONS = [0, 90, 180, 270];
const EDITOR_CAMERA = {
  position: [0, 0, 5] as [number, number, number],
  fov: 50,
};

// Type for rotation array (in radians)
type RotationArray = [number, number, number];

// Component to handle manual camera fitting
const CameraFitter = ({
  groupRef,
  controlsRef,
  loadedModel,
}: {
  groupRef: React.RefObject<Group | null>;
  controlsRef: React.RefObject<OrbitControlsImpl | null>;
  loadedModel: Object3D | null;
}) => {
  const { camera, invalidate, size: viewport } = useThree();

  useLayoutEffect(() => {
    // Fit after the async object is attached, not just the initially empty tree.
    // A transform/callback update keeps this identity, preserving the user's view.
    if (loadedModel && groupRef.current && "fov" in camera) {
      // Calculate bounding box of the group
      groupRef.current.updateWorldMatrix(true, true);
      const box = new Box3().setFromObject(groupRef.current);
      if (box.isEmpty()) return;
      const size = box.getSize(new Vector3());
      const center = box.getCenter(new Vector3());
      if (!size.toArray().concat(center.toArray()).every(Number.isFinite)) {
        return;
      }

      // Calculate distance to fit the object
      const aspect = viewport.width / Math.max(viewport.height, 1);
      const maxDim = Math.max(size.x / Math.max(aspect, 0.01), size.y);
      const fov = (camera as PerspectiveCamera).fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2)) + size.z / 2;

      // Add some margin
      cameraZ = Math.max(cameraZ * 1.5, camera.near * 2);
      camera.far = Math.max(camera.far, cameraZ + size.length());
      camera.updateProjectionMatrix();

      // Set camera position immediately without animation
      camera.position.set(center.x, center.y, center.z + cameraZ);
      camera.lookAt(center);
      camera.updateMatrixWorld();

      // Align OrbitControls target to the fitted center to avoid perceived damping/inertia
      if (controlsRef.current) {
        controlsRef.current.target.copy(center);
        controlsRef.current.update();
      }
      invalidate();
    }
  }, [
    camera,
    groupRef,
    controlsRef,
    loadedModel,
    viewport.width,
    viewport.height,
    invalidate,
  ]);

  return null;
};

/** Context meshes are outside the product store's geometry-change signals. */
const MeshEditorContext = ({ children }: { children: ReactNode }) => {
  const { scene, invalidate } = useThree();
  useLayoutEffect(() => {
    void children;
    markProductSceneChanged(scene);
    invalidate();
    return () => {
      markProductSceneChanged(scene);
      invalidate();
    };
  }, [children, scene, invalidate]);
  return <>{children}</>;
};

// Internal Model component
const Model = ({
  url,
  materialUrl,
  transform,
  materialOverride,
  /** When true and no materialOverride: keep GLTF materials (like SupplyPartRenderer without a forced material). */
  preferLoadedMeshMaterials = false,
  fallbackMaterial,
  baseRotationX = 0,
  onModelLoaded,
  onBoundsMeasured,
  referenceGuideType,
  onCtrlClick,
}: {
  url: string;
  materialUrl?: string;
  transform: {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: number;
  };
  materialOverride?: Material;
  preferLoadedMeshMaterials?: boolean;
  fallbackMaterial?: Material;
  baseRotationX?: number;
  onModelLoaded?: (model: Object3D | null) => void;
  onBoundsMeasured?: (bounds: IMeshBounds | null) => void;
  referenceGuideType: ReferenceGuideType;
  onCtrlClick?: (worldPoint: Vector3, worldNormal: Vector3 | null) => void;
}) => {
  const groupRef = useRef<Group>(null);
  const resourcesRef = useRef<MeshEditorModelResources | null>(null);
  const { scene, invalidate } = useThree();
  const callbacksRef = useRef({ onModelLoaded, onBoundsMeasured, onCtrlClick });
  useLayoutEffect(() => {
    callbacksRef.current = { onModelLoaded, onBoundsMeasured, onCtrlClick };
  }, [onModelLoaded, onBoundsMeasured, onCtrlClick]);

  const [px, py, pz] = transform.position;
  const [rx, ry, rz] = transform.rotation;
  const scale = transform.scale;
  const updateTransform = useCallback(
    (resources: MeshEditorModelResources) => {
      resources.update(
        { position: [px, py, pz], rotation: [rx, ry, rz], scale },
        baseRotationX,
        referenceGuideType,
      );
    },
    [px, py, pz, rx, ry, rz, scale, baseRotationX, referenceGuideType],
  );
  const updateTransformRef = useRef(updateTransform);
  useLayoutEffect(() => {
    updateTransformRef.current = updateTransform;
    if (!resourcesRef.current) return;
    updateTransform(resourcesRef.current);
    markProductSceneChanged(scene);
    invalidate();
  }, [updateTransform, scene, invalidate]);

  // An unused fallback/preference change must not replace an active override.
  const material =
    materialOverride ??
    (preferLoadedMeshMaterials ? undefined : fallbackMaterial);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    let cancelled = false;
    let resources: MeshEditorModelResources | null = null;

    const loadModel = async () => {
      try {
        const loadedObject = await ThreeToolsInstance.loadMesh({
          model: url,
          materialUrl,
        });
        // The late clone owns no GPU resources. Do not touch its borrowed assets.
        if (cancelled) return;

        if (!loadedObject) {
          console.warn(
            "[MeshEditor/Model] Mesh konnte nicht geladen werden:",
            url,
          );
        } else {
          resources = createMeshEditorModel(loadedObject, material);
          if (cancelled) {
            resources.dispose();
            return;
          }
          group.add(resources.object);
          updateTransformRef.current(resources);
          resourcesRef.current = resources;
        }

        markProductSceneChanged(scene);
        invalidate();
        callbacksRef.current.onBoundsMeasured?.(resources?.bounds ?? null);
        if (!cancelled) {
          callbacksRef.current.onModelLoaded?.(resources?.object ?? null);
        }
      } catch (error) {
        resources?.dispose();
        if (resourcesRef.current === resources) resourcesRef.current = null;
        resources = null;
        if (cancelled) return;
        console.error("Error loading model:", error);
        markProductSceneChanged(scene);
        invalidate();
        callbacksRef.current.onBoundsMeasured?.(null);
        if (!cancelled) callbacksRef.current.onModelLoaded?.(null);
      }
    };

    void loadModel();

    return () => {
      cancelled = true;
      if (resourcesRef.current === resources) resourcesRef.current = null;
      resources?.dispose();
      markProductSceneChanged(scene);
      invalidate();
      if (resources) callbacksRef.current.onModelLoaded?.(null);
    };
  }, [url, materialUrl, material, scene, invalidate]);

  const handleCtrlClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      if (!e.nativeEvent.ctrlKey) return;
      e.stopPropagation();

      const worldPoint = e.point;
      let worldNormal: Vector3 | null = null;

      if (e.face) {
        const normalMatrix = new Matrix3().getNormalMatrix(
          e.object.matrixWorld,
        );
        worldNormal = e.face.normal
          .clone()
          .applyMatrix3(normalMatrix)
          .normalize();
      }

      callbacksRef.current.onCtrlClick?.(worldPoint, worldNormal);
      invalidate();
    },
    [invalidate],
  );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: R3F group receives bubbled events from child meshes
    <group ref={groupRef} onClick={handleCtrlClick} dispose={null} />
  );
};

type Axis = "x" | "y" | "z";
type AxisType = false | Axis;
type Position = [number, number, number];
type Transform = MeshEditorTransform;
type BeginAxisDragFn = (axis: Axis, startPoint: Vector3) => void;

// Props for the MeshEditorView component
export interface MeshEditorViewProps {
  url: string;
  materialUrl?: string;
  transform: Transform;
  contextScene?: ReactNode; // Additional 3D content to render alongside the mesh
  materialOverride?: Material;
  /**
   * When true and no materialOverride: use GLTF materials (convertToModernMaterial)
   * instead of the shiny metal placeholder — matches SupplyPartRenderer without a forced material.
   */
  preferLoadedMeshMaterials?: boolean;
  /**
   * Material applied when there is no `materialOverride` and
   * `preferLoadedMeshMaterials` is false (e.g. the sheet metal material for
   * corners). Provided by the caller so material definitions stay in a single
   * source of truth (`manufacturer.materials`).
   */
  fallbackMaterial?: Material;
  enableInteractiveControls?: boolean; // Whether to enable interactive pivot controls
  baseRotationX?: number; // base X rotation (radians) applied before editor rotation
  onPositionChange?: (position: Position) => void;
  onModelLoaded?: (model: Object3D | null) => void; // Callback when model is loaded
  onBoundsMeasured?: (bounds: IMeshBounds | null) => void; // Callback when mesh bounds are measured
  modelRef?: React.RefObject<Object3D | null>; // Ref to the loaded model for axis direction calculation
  referenceGuideType: ReferenceGuideType; // Reference guide type to determine if 180° X rotation should be applied
}

// Component inside Canvas to provide axis drag handlers with access to Three.js context
const AxisDragProvider = ({
  axisDragRef,
  dragStartPointRef,
  dragStartDomainRef,
  onPositionChange,
  transform,
  onBeginAxisDrag,
  onEndAxisDrag,
  beginAxisDragRef,
  modelRef,
  baseRotationX = 0,
  referenceGuideType,
}: {
  axisDragRef: React.RefObject<AxisType>;
  dragStartPointRef: React.RefObject<Vector3 | null>;
  dragStartDomainRef: React.RefObject<Position | null>;
  onPositionChange?: (position: Position) => void;
  transform: Transform;
  onBeginAxisDrag: (axis: Axis) => void;
  onEndAxisDrag: () => void;
  beginAxisDragRef: React.RefObject<BeginAxisDragFn | null>;
  modelRef?: React.RefObject<Object3D | null>;
  baseRotationX?: number;
  referenceGuideType: ReferenceGuideType;
}) => {
  const { camera, gl, invalidate } = useThree();
  const raycasterRef = useRef(new Raycaster());
  const listenerCleanupRef = useRef<(() => void) | null>(null);
  /**
   * Raycast from screen position onto a plane.
   */
  const raycastOntoPlane = useCallback(
    (clientX: number, clientY: number, plane: Plane): Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycasterRef.current.setFromCamera(ndc, camera);
      const ray = raycasterRef.current.ray;
      const intersection = new Vector3();
      const result = ray.intersectPlane(plane, intersection);
      return result;
    },
    [camera, gl.domElement],
  );

  /**
   * Converts a world space delta to transform.position delta, accounting for
   * the conditional 180° X rotation and baseRotationX.
   */
  const shouldApplyFinalRotation =
    referenceGuideType !== ReferenceGuideType.HorizontalPlane &&
    referenceGuideType !== ReferenceGuideType.VerticalPlane;

  const worldDeltaToTransformDelta = useCallback(
    (worldDelta: Vector3): Position => {
      // Undo the 180° X rotation applied during rendering (flips Y and Z)
      const correctedY = shouldApplyFinalRotation
        ? -worldDelta.y
        : worldDelta.y;
      const correctedZ = shouldApplyFinalRotation
        ? -worldDelta.z
        : worldDelta.z;

      const cosBaseRot = Math.cos(-baseRotationX);
      const sinBaseRot = Math.sin(-baseRotationX);

      const localY = correctedY * cosBaseRot - correctedZ * sinBaseRot;
      const localZ = correctedY * sinBaseRot + correctedZ * cosBaseRot;

      return [-worldDelta.x, localZ, -localY];
    },
    [baseRotationX, shouldApplyFinalRotation],
  );

  /**
   * Gets the world space direction of a local axis from the model.
   * This accounts for the model's rotation and normalization.
   */
  const getWorldAxisDirection = useCallback(
    (axis: Axis): Vector3 => {
      if (!modelRef?.current) {
        // Fallback to world axes if model is not available
        if (axis === "x") {
          return new Vector3(1, 0, 0);
        }
        if (axis === "y") {
          return new Vector3(0, 1, 0);
        }
        return new Vector3(0, 0, 1);
      }

      // Use the shared function from meshAutoAlign
      return getWorldAxisDirectionFromModel(modelRef.current, axis);
    },
    [modelRef],
  );

  /**
   * Global pointer move handler for axis drag (attached to window during drag).
   */
  const handleGlobalPointerMove = useCallback(
    (event: PointerEvent) => {
      const currentAxis = axisDragRef.current;
      if (
        !currentAxis ||
        !dragStartPointRef.current ||
        !dragStartDomainRef.current ||
        !onPositionChange
      ) {
        return;
      }

      // Create a plane that faces the camera for better interaction
      const cameraDirection = camera.position
        .clone()
        .sub(dragStartPointRef.current)
        .normalize();

      const plane = new Plane();
      plane.setFromNormalAndCoplanarPoint(
        cameraDirection,
        dragStartPointRef.current,
      );

      const intersectionPoint = raycastOntoPlane(
        event.clientX,
        event.clientY,
        plane,
      );

      if (!intersectionPoint) {
        return;
      }

      // Get the world space direction of the axis from the model
      const axisDir = getWorldAxisDirection(currentAxis);

      const diff = intersectionPoint.clone().sub(dragStartPointRef.current);
      const delta = diff.dot(axisDir);

      const start = dragStartDomainRef.current;

      // Convert world space delta to transform.position delta
      // The base coordinate transformation (without baseRotationX) is:
      // World X = -transform.position[0]
      // World Y = transform.position[1] (but stored at index 2 in transform.position)
      // World Z = -transform.position[2] (but stored at index 1 in transform.position)
      // So: transform.position[0] = -World X, transform.position[1] = World Z, transform.position[2] = -World Y
      //
      // However, baseRotationX rotates the model around the X-axis, which changes how Y and Z map to transform.position.
      // We need to account for this rotation when converting world space movement to transform.position.
      const worldDelta = axisDir.clone().multiplyScalar(delta);
      const transformDelta = worldDeltaToTransformDelta(worldDelta);

      // Apply the delta to the start position
      const newPos: Position = [
        start[0] + transformDelta[0],
        start[1] + transformDelta[1],
        start[2] + transformDelta[2],
      ];
      onPositionChange(newPos);
      invalidate();
    },
    [
      axisDragRef,
      dragStartPointRef,
      dragStartDomainRef,
      onPositionChange,
      raycastOntoPlane,
      camera,
      getWorldAxisDirection,
      worldDeltaToTransformDelta,
      invalidate,
    ],
  );

  /**
   * Global pointer up handler for axis drag.
   */
  const handleGlobalPointerUp = useCallback(
    (_event: PointerEvent) => {
      if (!axisDragRef.current) {
        return;
      }

      // Remove event listeners FIRST
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
        listenerCleanupRef.current = null;
      }

      onEndAxisDrag();
      invalidate();
    },
    [axisDragRef, onEndAxisDrag, invalidate],
  );

  // A drag keeps its native listeners, but uses the latest committed callbacks.
  const pointerHandlersRef = useRef({
    move: handleGlobalPointerMove,
    up: handleGlobalPointerUp,
  });
  useLayoutEffect(() => {
    pointerHandlersRef.current = {
      move: handleGlobalPointerMove,
      up: handleGlobalPointerUp,
    };
  }, [handleGlobalPointerMove, handleGlobalPointerUp]);

  // Function to begin axis drag - sets up listeners immediately
  const beginAxisDrag = useCallback(
    (axis: Axis, startPoint: Vector3) => {
      const pos = transform?.position ?? [0, 0, 0];

      axisDragRef.current = axis;
      dragStartDomainRef.current = [pos[0], pos[1], pos[2]];
      dragStartPointRef.current = startPoint.clone();

      // Attach listeners immediately (don't wait for useEffect) - same pattern as DraggablePart
      // Create wrapper functions to ensure they work correctly
      const moveWrapper = (e: PointerEvent) => {
        pointerHandlersRef.current.move(e);
      };
      const upWrapper = (e: PointerEvent) => {
        pointerHandlersRef.current.up(e);
      };

      listenerCleanupRef.current?.();
      globalThis.addEventListener("pointermove", moveWrapper, {
        capture: true,
      });
      globalThis.addEventListener("pointerup", upWrapper, { capture: true });
      globalThis.addEventListener("pointercancel", upWrapper, {
        capture: true,
      });
      gl.domElement.addEventListener("pointermove", moveWrapper, {
        capture: true,
      });
      gl.domElement.addEventListener("pointerup", upWrapper, { capture: true });

      // Store cleanup for listeners - need to store the wrapper functions
      const cleanup = () => {
        globalThis.removeEventListener("pointermove", moveWrapper, {
          capture: true,
        });
        globalThis.removeEventListener("pointerup", upWrapper, {
          capture: true,
        });
        globalThis.removeEventListener("pointercancel", upWrapper, {
          capture: true,
        });
        gl.domElement.removeEventListener("pointermove", moveWrapper, {
          capture: true,
        });
        gl.domElement.removeEventListener("pointerup", upWrapper, {
          capture: true,
        });
      };

      listenerCleanupRef.current = cleanup;

      onBeginAxisDrag(axis);
      invalidate();
    },
    [
      transform,
      axisDragRef,
      dragStartPointRef,
      dragStartDomainRef,
      gl.domElement,
      onBeginAxisDrag,
      invalidate,
    ],
  );

  useLayoutEffect(() => {
    beginAxisDragRef.current = beginAxisDrag;
    return () => {
      beginAxisDragRef.current = null;
    };
  }, [beginAxisDrag, beginAxisDragRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
        listenerCleanupRef.current = null;
      }
      if (axisDragRef.current) onEndAxisDrag();
    };
  }, [axisDragRef, onEndAxisDrag]);

  return null;
};

export const MeshEditorView = ({
  url,
  materialUrl,
  transform,
  contextScene,
  materialOverride,
  preferLoadedMeshMaterials = false,
  fallbackMaterial,
  enableInteractiveControls = false,
  baseRotationX = 0,
  onPositionChange,
  onModelLoaded,
  onBoundsMeasured,
  modelRef,
  referenceGuideType,
}: MeshEditorViewProps): React.JSX.Element => {
  const groupRef = useRef<Group>(null);
  const orbitControlsRef = useRef<OrbitControlsImpl>(null);
  const canvasStateRef = useRef<RootState | null>(null);
  const [loadedModel, setLoadedModel] = useState<Object3D | null>(null);
  const modelPropsRef = useRef({ onModelLoaded, modelRef });
  const [isDragging, setIsDragging] = useState(false);
  const [isAxisDragging, setIsAxisDragging] = useState<AxisType>(false);
  const axisDragRef = useRef<AxisType>(false);
  const dragStartDomainRef = useRef<Position | null>(null);
  const dragStartPointRef = useRef<Vector3 | null>(null);
  const beginAxisDragRef = useRef<BeginAxisDragFn | null>(null);

  useLayoutEffect(() => {
    modelPropsRef.current = { onModelLoaded, modelRef };
    if (!modelRef) return;
    modelRef.current = loadedModel;
    return () => {
      // A replaced ref must not retain an object this editor later disposes.
      if (modelRef.current === loadedModel) modelRef.current = null;
    };
  }, [onModelLoaded, modelRef, loadedModel]);

  const handleModelLoaded = useCallback((model: Object3D | null) => {
    const latest = modelPropsRef.current;
    if (latest.modelRef) latest.modelRef.current = model;
    setLoadedModel(model);
    latest.onModelLoaded?.(model);
  }, []);

  const handleCreated = useCallback((state: RootState) => {
    canvasStateRef.current = state;
    state.invalidate();
  }, []);
  const invalidateCanvas = useCallback(() => {
    canvasStateRef.current?.invalidate();
  }, []);
  const markCanvasChanged = useCallback(() => {
    const state = canvasStateRef.current;
    if (!state) return;
    markProductSceneChanged(state.scene);
    state.invalidate();
  }, []);

  const endAxisDrag = useCallback(() => {
    if (!axisDragRef.current) return;
    axisDragRef.current = false;
    setIsAxisDragging(false);
    dragStartPointRef.current = null;
    dragStartDomainRef.current = null;
    if (orbitControlsRef.current) orbitControlsRef.current.enabled = true;
    invalidateCanvas();
  }, [invalidateCanvas]);

  const handleBeginAxisDrag = useCallback(
    (axis: Axis) => {
      setIsAxisDragging(axis);
      if (orbitControlsRef.current) orbitControlsRef.current.enabled = false;
      invalidateCanvas();
    },
    [invalidateCanvas],
  );

  // Handle drag state changes
  const handleDragStart = useCallback(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = false;
    }
    setIsDragging(true);
    invalidateCanvas();
  }, [invalidateCanvas]);

  const handleDragEnd = useCallback(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = true;
    }
    setIsDragging(false);
    markCanvasChanged();
  }, [markCanvasChanged]);

  useLayoutEffect(() => {
    // Removing PivotControls mid-drag does not deliver its onDragEnd callback.
    if (!enableInteractiveControls && isDragging) handleDragEnd();
  }, [enableInteractiveControls, isDragging, handleDragEnd]);

  const AXIS_SNAP_THRESHOLD = 0.85;

  const shouldApplyFinalRotation =
    referenceGuideType !== ReferenceGuideType.HorizontalPlane &&
    referenceGuideType !== ReferenceGuideType.VerticalPlane;

  const handleModelCtrlClick = useCallback(
    (worldPoint: Vector3, worldNormal: Vector3 | null) => {
      if (!worldNormal || !onPositionChange) return;

      const absX = Math.abs(worldNormal.x);
      const absY = Math.abs(worldNormal.y);
      const absZ = Math.abs(worldNormal.z);
      const maxComponent = Math.max(absX, absY, absZ);

      if (maxComponent < AXIS_SNAP_THRESHOLD) return;

      let worldDelta: Vector3;
      if (absX === maxComponent) {
        worldDelta = new Vector3(-worldPoint.x, 0, 0);
      } else if (absY === maxComponent) {
        worldDelta = new Vector3(0, -worldPoint.y, 0);
      } else {
        worldDelta = new Vector3(0, 0, -worldPoint.z);
      }

      const correctedY = shouldApplyFinalRotation
        ? -worldDelta.y
        : worldDelta.y;
      const correctedZ = shouldApplyFinalRotation
        ? -worldDelta.z
        : worldDelta.z;

      const cosBaseRot = Math.cos(-baseRotationX);
      const sinBaseRot = Math.sin(-baseRotationX);
      const localY = correctedY * cosBaseRot - correctedZ * sinBaseRot;
      const localZ = correctedY * sinBaseRot + correctedZ * cosBaseRot;
      const transformDelta: Position = [-worldDelta.x, localZ, -localY];

      const pos = transform?.position ?? [0, 0, 0];
      const newPos: Position = [
        pos[0] + transformDelta[0],
        pos[1] + transformDelta[1],
        pos[2] + transformDelta[2],
      ];

      onPositionChange(newPos);
    },
    [
      onPositionChange,
      transform?.position,
      baseRotationX,
      shouldApplyFinalRotation,
    ],
  );

  // Basic loading check (could be refined based on actual loading state management)
  if (!url || !transform) {
    return (
      <div className="flex h-full items-center justify-center">
        {translate("app.emerge.meshEditor.loading", "Mesh wird geladen...")}
      </div>
    );
  }

  return (
    <div className="flex-1 relative h-full w-full">
      <Canvas
        camera={EDITOR_CAMERA}
        frameloop="demand"
        onCreated={handleCreated}
        className="block h-full w-full"
        onPointerMissed={() => {
          // Don't end axis dragging when pointer misses - allow dragging outside canvas
        }}
      >
        <OrbitControls
          ref={orbitControlsRef}
          enabled={!isDragging && !isAxisDragging}
          enableDamping={false}
          dampingFactor={0}
          rotateSpeed={1}
          panSpeed={1}
          zoomSpeed={1}
          onChange={invalidateCanvas}
          onStart={invalidateCanvas}
          onEnd={invalidateCanvas}
        />
        {/* Same lighting + environment as the product viewer (single source of
            truth) so meshes are lit identically. No ground shadow: meshes here
            are shown free-floating, not standing on a floor.

            Wrapped because the stage suspends while the studio HDRI loads and
            R3F rethrows that suspension into the DOM tree; this page has no
            boundary of its own above the canvas. */}
        <Suspense fallback={null}>
          <ProductViewerStage groundShadow={false} />
        </Suspense>

        <group>
          {enableInteractiveControls ? (
            <PivotControls
              scale={100}
              onDragStart={handleDragStart}
              onDrag={markCanvasChanged}
              onDragEnd={handleDragEnd}
            >
              <group ref={groupRef}>
                <Model
                  url={url}
                  materialUrl={materialUrl}
                  transform={transform}
                  materialOverride={materialOverride}
                  preferLoadedMeshMaterials={preferLoadedMeshMaterials}
                  fallbackMaterial={fallbackMaterial}
                  baseRotationX={baseRotationX}
                  referenceGuideType={referenceGuideType}
                  onBoundsMeasured={onBoundsMeasured}
                  onModelLoaded={handleModelLoaded}
                  onCtrlClick={handleModelCtrlClick}
                />
                <MeshEditorContext>{contextScene}</MeshEditorContext>
              </group>
            </PivotControls>
          ) : (
            <group ref={groupRef}>
              <group>
                {/* Invisible larger hitbox for easier clicking */}
                <mesh
                  {...{
                    position: [50, 0, 0] as [number, number, number],
                    rotation: [0, 0, Math.PI / 2] as [number, number, number],
                  }}
                  onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (beginAxisDragRef.current) {
                      beginAxisDragRef.current("x", e.point);
                    }
                  }}
                  onPointerEnter={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0.3;
                      e.object.material.transparent = true;
                      invalidateCanvas();
                    }
                  }}
                  onPointerLeave={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0;
                      invalidateCanvas();
                    }
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [8, 8, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial
                    color="#ff0000"
                    {...{ transparent: true, opacity: 0 }}
                  />
                </mesh>
                {/* Visible smaller cylinder */}
                <mesh
                  {...{
                    position: [50, 0, 0] as [number, number, number],
                    rotation: [0, 0, Math.PI / 2] as [number, number, number],
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [2, 2, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial color="#ff0000" />
                </mesh>
                {/* Invisible larger hitbox for easier clicking */}
                <mesh
                  {...{ position: [0, 50, 0] as [number, number, number] }}
                  onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (beginAxisDragRef.current) {
                      beginAxisDragRef.current("y", e.point);
                    }
                  }}
                  onPointerEnter={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0.3;
                      e.object.material.transparent = true;
                      invalidateCanvas();
                    }
                  }}
                  onPointerLeave={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0;
                      invalidateCanvas();
                    }
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [8, 8, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial
                    color="#00ff00"
                    {...{ transparent: true, opacity: 0 }}
                  />
                </mesh>
                {/* Visible smaller cylinder */}
                <mesh {...{ position: [0, 50, 0] as [number, number, number] }}>
                  <cylinderGeometry
                    {...{
                      args: [2, 2, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial color="#00ff00" />
                </mesh>
                {/* Invisible larger hitbox for easier clicking */}
                <mesh
                  {...{
                    position: [0, 0, 50] as [number, number, number],
                    rotation: [Math.PI / 2, 0, 0] as [number, number, number],
                  }}
                  onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (beginAxisDragRef.current) {
                      beginAxisDragRef.current("z", e.point);
                    }
                  }}
                  onPointerEnter={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0.3;
                      e.object.material.transparent = true;
                      invalidateCanvas();
                    }
                  }}
                  onPointerLeave={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0;
                      invalidateCanvas();
                    }
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [8, 8, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial
                    color="#0000ff"
                    {...{ transparent: true, opacity: 0 }}
                  />
                </mesh>
                {/* Visible smaller cylinder */}
                <mesh
                  {...{
                    position: [0, 0, 50] as [number, number, number],
                    rotation: [Math.PI / 2, 0, 0] as [number, number, number],
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [2, 2, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial color="#0000ff" />
                </mesh>
              </group>
              <Model
                url={url}
                materialUrl={materialUrl}
                transform={transform}
                materialOverride={materialOverride}
                preferLoadedMeshMaterials={preferLoadedMeshMaterials}
                fallbackMaterial={fallbackMaterial}
                baseRotationX={baseRotationX}
                referenceGuideType={referenceGuideType}
                onBoundsMeasured={onBoundsMeasured}
                onModelLoaded={handleModelLoaded}
                onCtrlClick={handleModelCtrlClick}
              />
              <MeshEditorContext>{contextScene}</MeshEditorContext>
            </group>
          )}
        </group>

        <CameraFitter
          groupRef={groupRef}
          controlsRef={orbitControlsRef}
          loadedModel={loadedModel}
        />
        {!enableInteractiveControls && (
          <AxisDragProvider
            axisDragRef={axisDragRef}
            dragStartPointRef={dragStartPointRef}
            dragStartDomainRef={dragStartDomainRef}
            onPositionChange={onPositionChange}
            transform={transform}
            onBeginAxisDrag={handleBeginAxisDrag}
            onEndAxisDrag={endAxisDrag}
            beginAxisDragRef={beginAxisDragRef}
            modelRef={modelRef}
            baseRotationX={baseRotationX}
            referenceGuideType={referenceGuideType}
          />
        )}
      </Canvas>
    </div>
  );
};

export type { OrientationData, RotationArray };
// Export helper constants/types if needed by the container or other components
export { ORIENTATIONS, ROTATIONS };
